import React, { useState } from 'react';
import { Smartphone, Share, Plus, Download, X } from 'lucide-react';
import { usePwaInstall } from '../../hooks/usePwaInstall';

const DISMISS_KEY = 'pb_pwa_card_dismissed';

const readDismissed = (): boolean => {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};

export const PwaInstallCard: React.FC = () => {
  const { isStandalone, platform, canPrompt, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);
  const [busy, setBusy] = useState<boolean>(false);

  if (isStandalone || dismissed) return null;
  if (platform !== 'ios' && platform !== 'android' && !canPrompt) return null;

  const dismiss = (): void => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleInstall = async (): Promise<void> => {
    if (!canPrompt) return;
    setBusy(true);
    const outcome = await promptInstall();
    setBusy(false);
    if (outcome === 'accepted') setDismissed(true);
  };

  const renderInstructions = (): React.ReactNode => {
    if (platform === 'ios') {
      return (
        <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
          In Safari, tap{' '}
          <span className="inline-flex translate-y-0.5 items-center gap-1 rounded-md bg-surface-container-high px-1.5 py-0.5 text-xs font-semibold text-on-surface">
            <Share size={12} /> Share
          </span>
          {' '}then{' '}
          <span className="inline-flex translate-y-0.5 items-center gap-1 rounded-md bg-surface-container-high px-1.5 py-0.5 text-xs font-semibold text-on-surface">
            <Plus size={12} /> Add to Home Screen
          </span>
          .
        </p>
      );
    }
    if (canPrompt) {
      return (
        <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
          Install PaperBrain on your device — opens like an app, works offline-friendly.
        </p>
      );
    }
    return (
      <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
        Open your browser menu and choose <span className="font-semibold text-on-surface">Add to Home screen</span> to install PaperBrain.
      </p>
    );
  };

  return (
    <div
      className="pb-card relative mb-10 p-5 md:p-6"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="absolute right-3 top-3 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-on-surface-variant opacity-50 transition-opacity hover:opacity-100"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ background: 'var(--hl-sky)', color: 'var(--hl-sky-ink)' }}
        >
          <Smartphone size={20} />
        </span>
        <div className="min-w-0 flex-1 pr-6">
          <div className="mb-2 flex items-center gap-2">
            <span className="pb-eyebrow">
              One-tap access
            </span>
          </div>
          <h4 className="m-0 mb-1.5 font-headline text-xl italic text-on-surface">
            Keep PaperBrain a tap away
          </h4>
          {renderInstructions()}

          {canPrompt && (
            <button
              type="button"
              onClick={handleInstall}
              disabled={busy}
              className="pb-btn pb-btn--primary mt-4"
            >
              <Download size={14} />
              {busy ? 'Installing…' : 'Install app'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
